/**
 * Retrieves the current cart using XMLHttpRequest to avoid fetch interference.
 * @returns {Promise<Object>} - Resolves with the cart object.
 */
function getLiveCart() {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const cartUrl = window.routes?.cart_url ? `${window.routes.cart_url}.js` : '/cart.js';
    xhr.open('GET', cartUrl, true);
    xhr.setRequestHeader('Accept', 'application/json');
    xhr.onload = function () {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText));
        } catch (error) {
          reject(error);
        }
      } else {
        reject(new Error(`Cart fetch failed: ${xhr.status}`));
      }
    };
    xhr.onerror = function () {
      reject(new Error('XHR network error'));
    };
    xhr.send();
  });
}

/**
 * Fetches metafield data for a product variant.
 * @param {string} id - The variant ID to fetch metafield data for.
 * @param {string} namespace - The metafield namespace.
 * @param {string} key - The metafield key.
 * @param {string} token - The storefront access token.
 * @returns {Promise<Object>} - Returns the metafield data response.
 */
async function fetchMetafieldData(id, namespace, key, token) {
  const metafieldQuery = `
    query GetVariantMetafield($id: ID!, $namespace: String!, $key: String!) {
      node(id: $id) {
        ... on ProductVariant {
          id
          metafield(namespace: $namespace, key: $key) {
            reference {
              ... on Metaobject {
                handle
                type
                fields {
                  key
                  value
                }
              }
            }
          }
        }
      }
    }
  `;

  const variables = {
    id: 'gid:' + '/' + `/shopify/ProductVariant/${id}`,
    namespace,
    key
  };

  const res = await fetch('/api/graphql.json', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': token
    },
    body: JSON.stringify({
      query: metafieldQuery,
      variables
    })
  });
  return res.json();
}

/**
 * Function to find gift item in cart.
 * @param {Object} cart - The cart object.
 * @param {string} giftHandleIdentifier - Identifier string to find gift items.
 * @returns {Object|undefined} - Returns the gift item or undefined.
 */
function findGiftItem(cart, giftHandleIdentifier) {
  return cart.items.find(
    (item) => item.handle?.includes(giftHandleIdentifier) || item.url?.includes(giftHandleIdentifier)
  );
}

/**
 * Function to extract variant ID from gift item URL.
 * @param {Object} giftItem - The gift item object.
 * @returns {string|null} - The variant ID or null if not found.
 */
function getVariantIdFromGiftItem(giftItem) {
  if (!giftItem?.url) return null;
  const url = new URL(`${window.location.origin}${giftItem.url}`);
  return url.searchParams.get('variant');
}

/**
 *Function to extract vendorValue from metafield data response.
 * @param {Object} metafieldData - The metafield GraphQL response.
 * @returns {string|undefined} - The vendor value or undefined.
 */
function extractVendorValue(metafieldData) {
  return metafieldData?.data?.node?.metafield?.reference?.fields?.find((field) => field.key === 'internalName')?.value;
}

/**
 * Side-effect function: updates cart item properties via API call.
 * @param {Object} giftItem - The gift item to update.
 * @param {string} vendorValue - The vendor value to set.
 * @param {string} vendorPropertyKey - The cart property key to set.
 * @returns {Promise<void>}
 */
async function updateCartItemWithVendor(giftItem, vendorValue, vendorPropertyKey) {
  await fetch('/cart/change.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      id: giftItem.key,
      quantity: giftItem.quantity,
      properties: {
        ...giftItem.properties,
        [vendorPropertyKey]: vendorValue
      }
    })
  });
}

/**
 * Handles updating gift items in the cart with vendor routing information.
 * @param {Object} options - Configuration options for the gift item updater.
 * @param {string} options.storefrontToken - The Shopify Storefront API access token.
 * @param {string} options.metafieldNamespace - The namespace for the shipping metafield.
 * @param {string} options.metafieldKey - The key for the shipping metafield.
 * @param {string} options.vendorPropertyKey - The property key to set on the cart item.
 * @param {string} options.giftHandleIdentifier - The identifier to find gift items in cart.
 * @returns {Promise<void>} - Returns a promise that resolves when the update is complete.
 */
async function updateGiftItemWithVendorRouting({
  storefrontToken = window?.storefrontAccessToken?.bogos,
  metafieldNamespace = `app--${environmentAppId}--shipping`,
  metafieldKey = 'vendor',
  vendorPropertyKey = '_vendorRoutingMatchFree',
  giftHandleIdentifier = 'sca_clone_freegift'
} = {}) {
  try {
    const cart = await getLiveCart();
    if (!cart) return;

    const giftItem = findGiftItem(cart, giftHandleIdentifier);
    if (!giftItem) return;

    const variantId = getVariantIdFromGiftItem(giftItem);
    if (!variantId) return;

    const metafieldData = await fetchMetafieldData(variantId, metafieldNamespace, metafieldKey, storefrontToken);
    const vendorValue = extractVendorValue(metafieldData);
    if (!vendorValue) return;

    await updateCartItemWithVendor(giftItem, vendorValue, vendorPropertyKey);
  } catch {
    // Log errors for debugging purposes.
  }
}

/**
 * Initializes the gift item updater functionality.
 * Listens for cart and gift update events.
 */
function initializeGiftItemUpdater() {
  document.addEventListener('fg-gifts:updated', () => {
    updateGiftItemWithVendorRouting();
  });

  document.addEventListener('cart:updated', () => {
    updateGiftItemWithVendorRouting();
  });

  updateGiftItemWithVendorRouting();
}

// Run on DOM ready.
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeGiftItemUpdater);
} else {
  initializeGiftItemUpdater();
}
